import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import {
  blogCategories,
  blogCategoryPath,
  BlogCategorySlug,
  BlogPost,
  blogPostPath,
  formatBlogDate,
} from '../../content/blog/posts';
import { Button } from '../_atoms/Button';
import MaxWidthContainer from '../_atoms/MaxWidthContainer';

const MOBILE = '(max-width: 992px)';
const NARROW = '(max-width: 360px)';

type PillActive = 'all' | BlogCategorySlug;

export const FeaturedPostHero = ({ post }: { post: BlogPost }) => {
  const category = blogCategories.find(entry => entry.slug === post.category);
  return (
    <Hero>
      <MaxWidthContainer>
        <HeroGrid>
          <HeroCopy>
            {category && (
              <Link href={blogCategoryPath(category.slug)} passHref>
                <HeroTag>{category.name}</HeroTag>
              </Link>
            )}
            <HeroTitle>{post.title}</HeroTitle>
            <HeroExcerpt>{post.description}</HeroExcerpt>
            <HeroByline>
              By {post.author}
              <span aria-hidden="true"> | </span>
              <time dateTime={post.publishedAt}>{formatBlogDate(post.publishedAt)}</time>
            </HeroByline>
            <HeroActions>
              <Link href={blogPostPath(post.slug)} passHref>
                <a>
                  <HeroButton type="primary">Read the post</HeroButton>
                </a>
              </Link>
            </HeroActions>
          </HeroCopy>
          <Link href={blogPostPath(post.slug)} passHref>
            <HeroImageLink>
              <HeroImage src={post.cover} alt={post.coverAlt} />
            </HeroImageLink>
          </Link>
        </HeroGrid>
      </MaxWidthContainer>
    </Hero>
  );
};

export const CategoryHero = ({
  name,
  description,
}: {
  name: string;
  description: string;
}) => (
  <Hero>
    <MaxWidthContainer>
      <CategoryInner>
        <Breadcrumb>
          <Link href="/" passHref>
            <CrumbLink>Home</CrumbLink>
          </Link>
          <CrumbSep aria-hidden="true">/</CrumbSep>
          <Link href="/c/blogs" passHref>
            <CrumbLink>Blog</CrumbLink>
          </Link>
          <CrumbSep aria-hidden="true">/</CrumbSep>
          <CrumbCurrent>{name}</CrumbCurrent>
        </Breadcrumb>
        <HeroTitle>{name}</HeroTitle>
        <HeroExcerpt>{description}</HeroExcerpt>
      </CategoryInner>
    </MaxWidthContainer>
  </Hero>
);

export const BlogFeed = ({
  active,
  posts,
}: {
  active: PillActive;
  posts: BlogPost[];
}) => (
  <GridSection>
    <MaxWidthContainer>
      <FilterBar>
        <Link href="/c/blogs" passHref>
          <FilterPill $active={active === 'all'}>All</FilterPill>
        </Link>
        {blogCategories.map(category => (
          <Link key={category.slug} href={blogCategoryPath(category.slug)} passHref>
            <FilterPill $active={active === category.slug}>{category.name}</FilterPill>
          </Link>
        ))}
      </FilterBar>
      {posts.length === 0 ? (
        active === 'all' ? null : (
          <Empty>No posts in this category yet.</Empty>
        )
      ) : (
        <CardGrid>
          {posts.map(post => {
            const category = blogCategories.find(entry => entry.slug === post.category);
            return (
              <Link key={post.slug} href={blogPostPath(post.slug)} passHref>
                <Card as="a">
                  <CardImage src={post.cover} alt="" />
                  {category && <CardCategory>{category.name}</CardCategory>}
                  <CardTitle>{post.title}</CardTitle>
                  <CardExcerpt>{post.description}</CardExcerpt>
                  <CardFooter>
                    <time dateTime={post.publishedAt}>
                      {formatBlogDate(post.publishedAt)}
                    </time>
                    <ReadMore>Read post →</ReadMore>
                  </CardFooter>
                </Card>
              </Link>
            );
          })}
        </CardGrid>
      )}
    </MaxWidthContainer>
  </GridSection>
);

export const BlogClosingCta = () => (
  <CtaSection>
    <MaxWidthContainer>
      <CtaInner>
        <CtaTag>LEARNING TOGETHER</CtaTag>
        <CtaTitle>Bring writing practice into the room</CtaTitle>
        <CtaDescription>
          Curipod is teacher-led writing practice your class does together. Start a
          lesson, or book a walkthrough for your school.
        </CtaDescription>
        <CtaActions>
          <Link href="/signup" passHref>
            <a>
              <Button
                type="orange"
                styles={{
                  borderRadius: '20px',
                  padding: '0 28px',
                  fontSize: 18,
                  height: 52,
                }}
              >
                Get started for free
              </Button>
            </a>
          </Link>
          <Link href="/quotes?step=role" passHref>
            <a>
              <Button
                type="tertiary"
                styles={{
                  borderRadius: '20px',
                  padding: '0 28px',
                  fontSize: 18,
                  height: 52,
                  borderColor: theme.colors.orange,
                  color: theme.colors.orange,
                }}
              >
                Book a demo
              </Button>
            </a>
          </Link>
        </CtaActions>
      </CtaInner>
    </MaxWidthContainer>
  </CtaSection>
);

const Hero = styled.header`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroGrid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: 36px;
  align-items: center;
  padding: 72px 0 64px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    padding: 48px 0 40px;
    gap: 24px;
  }

  @media ${NARROW} {
    padding: 36px 0 32px;
  }
`;

const HeroCopy = styled.div`
  min-width: 0;
`;

const HeroTag = styled.a`
  display: inline-block;
  background: rgba(255, 255, 255, 0.25);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.4px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 16px;
  text-decoration: none;
  max-width: 100%;

  &:hover {
    color: ${theme.colors.white};
    background: rgba(255, 255, 255, 0.35);
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 12px;
  color: ${theme.colors.white};
  font-size: 46px;
  line-height: 1.12;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  overflow-wrap: anywhere;

  @media ${MOBILE} {
    font-size: 34px;
  }

  @media ${NARROW} {
    font-size: 28px;
  }
`;

const HeroExcerpt = styled.p`
  margin: 0 0 16px;
  color: rgba(255, 255, 255, 0.96);
  font-size: 18px;
  line-height: 1.6;
  font-weight: 500;
  max-width: 640px;

  @media ${NARROW} {
    font-size: 16px;
  }
`;

const HeroByline = styled.p`
  margin: 0;
  color: ${theme.colors.white};
  font-weight: 700;
  font-size: 15px;
`;

const HeroActions = styled.div`
  margin-top: 24px;
`;

const HeroButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 28px;
  font-size: 18px;
  height: 52px;
  font-weight: 800;

  &:hover {
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${NARROW} {
    width: 100%;
  }
`;

const HeroImageLink = styled.a`
  display: block;
  min-width: 0;
`;

const HeroImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 10px;
  background: ${theme.colors.white};
`;

const CategoryInner = styled.div`
  position: relative;
  z-index: 1;
  padding: 56px 0 48px;
  max-width: 760px;

  @media ${MOBILE} {
    padding: 40px 0 32px;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.4;
`;

const CrumbLink = styled.a`
  color: ${theme.colors.white};
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${theme.colors.white};
  }
`;

const CrumbSep = styled.span`
  color: rgba(255, 255, 255, 0.75);
`;

const CrumbCurrent = styled.span`
  color: ${theme.colors.white};
  font-weight: 700;
`;

const GridSection = styled.section`
  background-color: ${theme.colors.background};
  padding: 48px 0 72px;

  @media ${MOBILE} {
    padding: 32px 0 48px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;

  @media ${NARROW} {
    gap: 6px;
  }
`;

const FilterPill = styled.a<{ $active: boolean }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  text-decoration: none;
  border: 2px solid
    ${({ $active }) => ($active ? theme.colors.orange : theme.colors.beigeDark)};
  background: ${({ $active }) => ($active ? theme.colors.orange : theme.colors.white)};
  color: ${({ $active }) => ($active ? theme.colors.white : theme.colors.black)};

  &:hover {
    border-color: ${theme.colors.orange};
    color: ${({ $active }) => ($active ? theme.colors.white : theme.colors.black)};
    background: ${({ $active }) =>
      $active ? theme.colors.orangeDark : 'rgba(249, 151, 70, 0.08)'};
  }

  @media ${NARROW} {
    padding: 7px 12px;
    font-size: 13px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: 10px;
  padding: 16px 16px 20px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-top: 4px solid ${theme.colors.orange};
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  min-width: 0;
  height: 100%;
  transition: ${theme.effects.defaultTransition};

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-4px);
    color: inherit;
  }
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 10px;
  margin-bottom: 14px;
  background: ${theme.colors.paleBeige};
`;

const CardCategory = styled.p`
  margin: 0 0 8px;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const CardTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 20px;
  line-height: 1.3;
  color: ${theme.colors.black};
  overflow-wrap: anywhere;

  @media ${NARROW} {
    font-size: 18px;
  }
`;

const CardExcerpt = styled.p`
  margin: 0 0 16px;
  color: ${theme.colors.black};
  font-size: 15px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
  font-size: 14px;
  color: ${theme.colors.greyVeryDark};
`;

const ReadMore = styled.span`
  color: ${theme.colors.orangeDark};
  font-weight: 700;
  white-space: nowrap;
`;

const Empty = styled.p`
  margin: 0;
  padding: 24px 0 8px;
  font-size: 18px;
`;

const CtaSection = styled.section`
  background: ${theme.colors.paleBeige};
  padding: 64px 0 72px;

  @media ${MOBILE} {
    padding: 48px 0;
  }
`;

const CtaInner = styled.div`
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
`;

const CtaTag = styled.p`
  margin: 0 0 10px;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.5px;
`;

const CtaTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 36px;
  line-height: 1.2;
  overflow-wrap: anywhere;

  @media ${MOBILE} {
    font-size: 28px;
  }

  @media ${NARROW} {
    font-size: 24px;
  }
`;

const CtaDescription = styled.p`
  margin: 0 auto 24px;
  font-size: 18px;
  line-height: 1.6;
  max-width: 560px;

  @media ${NARROW} {
    font-size: 16px;
  }
`;

const CtaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;

  @media ${NARROW} {
    flex-direction: column;
    align-items: stretch;

    a {
      width: 100%;
    }

    button {
      width: 100%;
    }
  }
`;
